import type { Theme } from "@mui/material";
import {
  Box,
  Dialog,
  DialogContent,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { zodResolver } from "@hookform/resolvers/zod";
import * as React from "react";
import { useForm } from "react-hook-form";
import z from "zod";

import { DIALOG_WIDTH } from "../../constants";
import { CloseDialogHandler } from "../../contexts/DialogContext";
import { Schemas } from "../../schemas";
import { useUpdateDashboard } from "../../hooks/mutation/update/useUpdateDashboard";
import { useDashboard } from "../../hooks/query/useDashboard";
import StandardDialogActions from "./StandardDialogActions";
import StandardDialogTitle from "./StandardDialogTitle";

const schema = Schemas.dashboard.omit({
  id: true,
  metrics: true,
});

const EditDashboardDialog: React.FC<{
  onDialogClosed: CloseDialogHandler;
  dashboardId: string;
}> = ({ onDialogClosed, dashboardId }) => {
  const isSmallScreen = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("sm"),
  );
  const dashboardQuery = useDashboard(dashboardId);
  const { mutateAsync: updateDashboard, isPending: isUpdating } =
    useUpdateDashboard();
  const [submitError, setSubmitError] = React.useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: "",
      description: "",
    },
    mode: "onBlur",
  });

  React.useEffect(() => {
    const dashboard = dashboardQuery.data;
    if (!dashboard) {return;}

    reset({
      name: dashboard.name,
      description: dashboard.description ?? "",
    });
  }, [dashboardQuery.data, reset]);

  const onSubmit = async (data: z.infer<typeof schema>) => {
    try {
      setSubmitError(null);
      await updateDashboard({ dashboardId, dashboard: data });
      onDialogClosed({ message: "Dashboard updated!", severity: "success" });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Something went wrong.";
      setSubmitError(msg);
    }
  };

  const onClose = () => {
    if (isUpdating) {return;}
    setSubmitError(null);
    reset({ name: "", description: "" });
    onDialogClosed();
  };

  return (
    <Dialog
      open={true}
      onClose={onClose}
      fullWidth
      fullScreen={isSmallScreen}
      PaperProps={{
        sx: { maxWidth: isSmallScreen ? undefined : DIALOG_WIDTH.lg },
      }}
    >
      <StandardDialogTitle
        title={"Edit Dashboard"}
        isSmallScreen={isSmallScreen}
        onClose={onClose}
      />

      <Box
        component="form"
        id="edit-dashboard-form"
        onSubmit={handleSubmit(onSubmit)}
      >
        <DialogContent sx={{ overflowX: "hidden" }}>
          <Box sx={{ width: "100%" }}>
            <Stack sx={{ mb: 1.5 }}>
              <Typography variant="body2" color="text.secondary">
              Create a Control Plane and optionally declare its dependencies.
              </Typography>
            </Stack>

            <Divider sx={{ mb: 1.5 }} />

            <Stack spacing={2.25} sx={{ width: "100%" }}>
              {submitError && (
                <FormHelperText error sx={{ fontSize: 13, m: 0 }}>
                  {submitError}
                </FormHelperText>
              )}

              <FormControl>
                <FormLabel>Name</FormLabel>
                <TextField
                  size="small"
                  placeholder="e.g. Payments Service"
                  error={!!errors.name}
                  {...register("name", {
                    onChange: () => submitError && setSubmitError(null),
                  })}
                />
                <FormHelperText error={!!errors.name}>
                  {errors.name?.message ?? " "}
                </FormHelperText>
              </FormControl>

              <FormControl>
                <FormLabel>Description</FormLabel>
                <TextField
                  size="small"
                  multiline
                  minRows={2}
                  placeholder="Short description of the system"
                  error={!!errors.description}
                  {...register("description", {
                    onChange: () => submitError && setSubmitError(null),
                  })}
                />
                <FormHelperText error={!!errors.description}>
                  {errors.description?.message ?? " "}
                </FormHelperText>
              </FormControl>
            </Stack>
          </Box>
        </DialogContent>

        <StandardDialogActions
          primaryText={"Save"}
          primaryIcon={<></>}
          onCancel={onClose}
          loading={isUpdating}
          disabled={!dashboardQuery.data || isUpdating}
        />
      </Box>
    </Dialog>
  );
};

export default EditDashboardDialog;
